import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { opsApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';
import type { OpsCustomerRow } from '@/api/operations';

/**
 * Operations — cross-org resource inventory (instances, domains) for the Tech
 * console. Served by @mgmt `/internal/ops/{instances,domains}[/summary]`
 * (employee RPT, EmployeeRequired). Read-only, DB-only, customer-enriched.
 *
 * Distinct from `operations-category-hooks` (per-service instance/storage
 * proxies): these are FLAT lists across ALL orgs with server pagination.
 */

/* ── Row types ──────────────────────────────────────────────────────────── */

/** One row from `GET /internal/ops/instances` (flat, cross-org). */
export interface OpsInstanceListRow {
  id: string;
  name: string;
  uid: string;
  status: string;
  provider_name: string;
  organization_id: string;
  service_zone_id?: string;
  /** JSONB spec: cpu / memory / ip details (no secrets). */
  spec?: Record<string, unknown> | null;
  synchronized_at?: string | null;
  created_at: string;
  customer: OpsCustomerRow | null;
}

/** One row from `GET /internal/ops/domains` (flat, cross-org). */
export interface OpsDomainListRow {
  id: string;
  name: string;
  provider?: string;
  status: string;
  organization_id: string;
  expired_at?: string | null;
  synchronized_at?: string | null;
  created_at: string;
  customer: OpsCustomerRow | null;
}

/* ── Summary types ──────────────────────────────────────────────────────── */

/** A {key,count} bucket in a resource summary breakdown. */
export interface OpsCountKV {
  key: string;
  count: number;
}

/** One top-consumer org in a resource summary. */
export interface OpsResourceTopOrg {
  organization_id: string;
  count: number;
  customer: OpsCustomerRow | null;
}

/**
 * Response from `/internal/ops/{instances,domains}/summary`.
 * Dashboard-style aggregate over the same filter as the list.
 */
export interface OpsResourceSummary {
  total: number;
  by_provider: OpsCountKV[];
  by_status: OpsCountKV[];
  org_count: number;
  top_orgs: OpsResourceTopOrg[];
}

/* ── Params ─────────────────────────────────────────────────────────────── */

export interface OpsResourceParams {
  page?: number;
  per_page?: number;
  /** Free-text search (instances: name/uid; domains: name). */
  q?: string;
  provider?: string;
  status?: string;
  /** Organization ID filter. */
  org_id?: string;
}

function buildQuery(params: OpsResourceParams): string {
  const clean: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) clean[k] = v;
  }
  return qs.stringify(clean);
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

export const opsResourceKeys = {
  instances: (p: OpsResourceParams) => ['ops', 'instances', p] as const,
  instancesSummary: (p: OpsResourceParams) => ['ops', 'instances', 'summary', p] as const,
  domains: (p: OpsResourceParams) => ['ops', 'domains', p] as const,
  domainsSummary: (p: OpsResourceParams) => ['ops', 'domains', 'summary', p] as const,
};

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/** `GET /internal/ops/instances` — flat cross-org instance inventory. */
export function useOpsInstances(params: OpsResourceParams = {}) {
  return useQuery({
    queryKey: opsResourceKeys.instances(params),
    queryFn: async (): Promise<ResponseArray<OpsInstanceListRow>> => {
      const res = await opsApi.get<ResponseArray<OpsInstanceListRow>>(
        `/internal/ops/instances?${buildQuery({ page: 1, per_page: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /internal/ops/instances/summary` — dashboard-style aggregate. */
export function useOpsInstancesSummary(params: OpsResourceParams = {}) {
  return useQuery({
    queryKey: opsResourceKeys.instancesSummary(params),
    queryFn: async (): Promise<OpsResourceSummary> => {
      const res = await opsApi.get<{ result: OpsResourceSummary }>(
        `/internal/ops/instances/summary?${buildQuery(params)}`,
      );
      return res.data.result;
    },
    staleTime: 60_000,
  });
}

/** `GET /internal/ops/domains` — flat cross-org domain inventory. */
export function useOpsDomains(params: OpsResourceParams = {}) {
  return useQuery({
    queryKey: opsResourceKeys.domains(params),
    queryFn: async (): Promise<ResponseArray<OpsDomainListRow>> => {
      const res = await opsApi.get<ResponseArray<OpsDomainListRow>>(
        `/internal/ops/domains?${buildQuery({ page: 1, per_page: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /internal/ops/domains/summary` — dashboard-style aggregate. */
export function useOpsDomainsSummary(params: OpsResourceParams = {}) {
  return useQuery({
    queryKey: opsResourceKeys.domainsSummary(params),
    queryFn: async (): Promise<OpsResourceSummary> => {
      const res = await opsApi.get<{ result: OpsResourceSummary }>(
        `/internal/ops/domains/summary?${buildQuery(params)}`,
      );
      return res.data.result;
    },
    staleTime: 60_000,
  });
}
